import React, { useEffect, useState } from 'react';
import { API, getToken } from './adminApi';

// Manage Communities → Update → "Watch the video" (community.aboutVideo and
// an optional cover image, aboutVideoPoster — components/AboutVideo.js).
// Files go straight from this browser to the bucket (community-videos/ in
// the photos bucket) with a short-lived signed link from the backend
// (routes/communityVideo.js) — with a progress bar — then "Save video"
// puts the addresses on the community. Saved on its own, apart from the
// page's other Save.
const MB = 1024 * 1024;

// PUT the file to the signed link, reporting progress (0–100).
const putWithProgress = (url, file, onProgress) => new Promise((resolve, reject) => {
  const xhr = new XMLHttpRequest();
  xhr.open('PUT', url);
  xhr.setRequestHeader('Content-Type', file.type);
  xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100)); };
  xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`)));
  xhr.onerror = () => reject(new Error('Upload failed — check your connection'));
  xhr.send(file);
});

const authed = async (path, options = {}) => {
  const token = await getToken();
  const res = await fetch(`${API}${path}`, { ...options, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...(options.headers || {}) } });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Something went wrong');
  return data;
};

const CommunityVideoSection = ({ community, onSaved }) => {
  const [video, setVideo] = useState(community.aboutVideo || '');
  const [poster, setPoster] = useState(community.aboutVideoPoster || '');
  const [label, setLabel] = useState(community.aboutVideoLabel || '');
  const [abTest, setAbTest] = useState(community.aboutVideoABTest === true);
  const [progress, setProgress] = useState(null); // { kind, pct }
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  // Another community picked: start from its saved video.
  useEffect(() => {
    setVideo(community.aboutVideo || '');
    setPoster(community.aboutVideoPoster || '');
    setLabel(community.aboutVideoLabel || '');
    setAbTest(community.aboutVideoABTest === true);
    setMsg('');
    setProgress(null);
  }, [community._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = video !== (community.aboutVideo || '') || poster !== (community.aboutVideoPoster || '') || label !== (community.aboutVideoLabel || '') || abTest !== (community.aboutVideoABTest === true);

  const upload = async (kind, file) => {
    if (!file) return;
    setMsg('');
    setBusy(true);
    setProgress({ kind, pct: 0 });
    try {
      const { uploadUrl, publicUrl } = await authed(`/admin/communities/${community._id}/video-upload`, {
        method: 'POST',
        body: JSON.stringify({ kind, contentType: file.type, size: file.size }),
      });
      await putWithProgress(uploadUrl, file, (pct) => setProgress({ kind, pct }));
      if (kind === 'video') setVideo(publicUrl); else setPoster(publicUrl);
      setMsg(`${kind === 'video' ? 'Video' : 'Cover image'} uploaded (${(file.size / MB).toFixed(1)} MB) — check the preview, then Save video.`);
    } catch (err) {
      setMsg(`Error: ${err.message}`);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const save = async (next = { aboutVideo: video, aboutVideoPoster: poster, aboutVideoLabel: label, aboutVideoABTest: abTest }) => {
    setBusy(true);
    setMsg('');
    try {
      await authed(`/admin/communities/${community._id}/video`, { method: 'PUT', body: JSON.stringify(next) });
      onSaved?.(next);
      setMsg(next.aboutVideo ? '✓ Saved — the "Watch the video" button is on the community page.' : '✓ Video removed from the community page.');
    } catch (err) {
      setMsg(`Error: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const remove = () => {
    setVideo('');
    setPoster('');
    save({ aboutVideo: '', aboutVideoPoster: '' });
  };

  return (
    <div>
      <h3 style={{ marginTop: '1.5rem' }}>Watch the video</h3>
      <p className="amc-hint">
        A video visitors can choose to play, with sound, from a "▶ Watch the video" button on the community page.
        MP4 works everywhere (WebM / MOV also accepted), up to 500 MB.
      </p>
      {video ? (
        <video key={video} src={video} poster={poster || undefined} controls playsInline preload="metadata" style={{ width: '100%', maxWidth: 420, borderRadius: 8, background: '#000', display: 'block' }} />
      ) : (
        <p className="amc-hint">No video yet.</p>
      )}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center', marginTop: '0.6rem' }}>
        <label className="amc-hint" style={{ cursor: busy ? 'default' : 'pointer' }}>
          <strong>{video ? 'Replace video' : 'Upload video'}</strong>{' '}
          <input type="file" accept="video/mp4,video/webm,video/quicktime" disabled={busy} onChange={e => upload('video', e.target.files?.[0])} />
        </label>
        <label className="amc-hint" style={{ cursor: busy ? 'default' : 'pointer' }}>
          Cover image (optional){' '}
          <input type="file" accept="image/jpeg,image/png,image/webp" disabled={busy || !video} onChange={e => upload('poster', e.target.files?.[0])} />
        </label>
      </div>
      {progress && (
        <div style={{ marginTop: '0.6rem', maxWidth: 420 }} aria-live="polite">
          <div style={{ height: 8, borderRadius: 4, background: '#e2e8f0', overflow: 'hidden' }}>
            <div style={{ width: `${progress.pct}%`, height: '100%', background: '#4f46e5', transition: 'width 0.2s' }} />
          </div>
          <div className="amc-hint">Uploading {progress.kind === 'video' ? 'video' : 'cover image'}… {progress.pct}%</div>
        </div>
      )}
      <label className="amc-hint" style={{ display: 'block', marginTop: '0.6rem' }}>
        Button text{' '}
        <input value={label} maxLength={60} placeholder="Watch the video" disabled={busy} onChange={e => setLabel(e.target.value)} style={{ width: '16rem' }} />
      </label>
      <label className="amc-hint" style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', marginTop: '0.5rem', cursor: 'pointer' }}>
        <input type="checkbox" checked={abTest} disabled={busy} onChange={e => setAbTest(e.target.checked)} style={{ marginTop: '0.2rem' }} />
        <span>
          <strong>A/B test the video</strong> — logged-out visitors split three ways: A no video, B the "Watch the video" button over the hero, C the video's still in a hexagon under the title (tapping it plays it).
          Results in Admin → A/B Test ("Community page — Watch the video"); compare visits in Page Analytics with the Video filter.
        </span>
      </label>
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem' }}>
        <button type="button" className="as-btn" disabled={busy || !dirty || !video} onClick={() => save()}>Save video</button>
        {(community.aboutVideo || video) && <button type="button" className="as-btn" disabled={busy} onClick={remove}>Remove video</button>}
      </div>
      {msg && <p className="amc-hint" style={{ marginTop: '0.4rem' }}>{msg}</p>}
    </div>
  );
};

export default CommunityVideoSection;
